import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../config/api.js';
import { formatCurrency } from '../utils/formatters';

const AgentLifecycle = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('');
  const [cohortDetail, setCohortDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, agent: null });
  const [leftDate, setLeftDate] = useState('');
  const [error, setError] = useState(null);

  // All Agents tab state
  const [allAgents, setAllAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editForm, setEditForm] = useState({ created_at: '', status: '', left_date: '' });
  const [savingAgent, setSavingAgent] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchCohorts();
  }, []);

  useEffect(() => {
    if (activeTab === 1 && allAgents.length === 0) {
      fetchAllAgents();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedCohort) {
      fetchCohortDetail(selectedCohort);
    } else {
      setCohortDetail(null);
    }
  }, [selectedCohort]);

  const fetchCohorts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/retention/cohorts');
      setCohorts(res.data);
    } catch (err) {
      setError(t('agentLifecycle.failedToLoadCohorts'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCohortDetail = async (cohort) => {
    try {
      setDetailLoading(true);
      const res = await api.get(`/retention/cohort/${cohort}`);
      setCohortDetail(res.data);
    } catch (err) {
      setError(t('agentLifecycle.failedToLoadCohortDetails'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const { agent } = statusDialog;
    try {
      await api.patch(`/retention/agents/${agent.user_id}/status`, {
        status: newStatus,
        left_date: newStatus === 'inactive' ? leftDate : undefined,
      });
      setStatusDialog({ open: false, agent: null });
      setLeftDate('');
      fetchCohorts();
      if (selectedCohort) fetchCohortDetail(selectedCohort);
    } catch (err) {
      setError(t('agentLifecycle.failedToUpdateStatus'));
    }
  };

  const openStatusDialog = (agent) => {
    setStatusDialog({ open: true, agent });
    setLeftDate(
      agent.left_date
        ? new Date(agent.left_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
    );
  };

  // All Agents management
  const fetchAllAgents = async () => {
    try {
      setAgentsLoading(true);
      const res = await api.get('/retention/agents');
      setAllAgents(res.data);
    } catch (err) {
      setError(t('agentLifecycle.failedToLoadAgents'));
    } finally {
      setAgentsLoading(false);
    }
  };

  const startEditing = (agent) => {
    setEditingAgent(agent.user_id);
    setEditForm({
      created_at: agent.created_at
        ? new Date(agent.created_at).toISOString().split('T')[0]
        : '',
      status: agent.status || 'active',
      left_date: agent.left_date
        ? new Date(agent.left_date).toISOString().split('T')[0]
        : '',
    });
  };

  const cancelEditing = () => {
    setEditingAgent(null);
    setEditForm({ created_at: '', status: '', left_date: '' });
  };

  const saveAgent = async (userId) => {
    try {
      setSavingAgent(true);
      const payload = {};

      if (editForm.created_at) payload.created_at = editForm.created_at;
      payload.status = editForm.status;
      if (editForm.status === 'inactive') {
        if (!editForm.left_date) {
          setError(t('agentLifecycle.leftDateRequiredError'));
          setSavingAgent(false);
          return;
        }
        payload.left_date = editForm.left_date;
      } else {
        payload.left_date = null;
      }

      await api.patch(`/retention/agents/${userId}`, payload);
      setEditingAgent(null);
      setSnackbar({ open: true, message: t('agentLifecycle.agentUpdated'), severity: 'success' });
      fetchAllAgents();
      fetchCohorts();
    } catch (err) {
      setError(t('agentLifecycle.failedToUpdateAgent'));
    } finally {
      setSavingAgent(false);
    }
  };

  const renderRetentionCell = (data) => {
    if (!data.available) {
      return <Typography variant="body2" color="text.disabled">{t('common.tbd')}</Typography>;
    }
    if (data.rate === null || data.rate === undefined) {
      return <Typography variant="body2" color="text.disabled">{t('common.na')}</Typography>;
    }
    const color =
      data.rate >= 60 ? 'success.main' : data.rate >= 40 ? 'warning.main' : 'error.main';
    return (
      <Box>
        <Typography variant="body2" fontWeight="bold" color={color}>
          {data.rate}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({data.count} {t('agentLifecycle.agents')})
        </Typography>
      </Box>
    );
  };

  const renderChurnCell = (value) => {
    if (value === undefined || value === null) {
      return <Typography variant="body2" color="text.disabled">{t('common.tbd')}</Typography>;
    }
    const color = value > 30 ? 'error.main' : value > 15 ? 'warning.main' : 'success.main';
    return (
      <Typography variant="body2" fontWeight="bold" color={color}>
        {value}%
      </Typography>
    );
  };

  const selectedData = cohorts.find((c) => c.cohort === selectedCohort);

  const bestAvailableRetention = selectedData
    ? selectedData.retention['12mo'].available
      ? selectedData.retention['12mo']
      : selectedData.retention['6mo'].available
      ? selectedData.retention['6mo']
      : selectedData.retention['3mo'].available
      ? selectedData.retention['3mo']
      : null
    : null;

  const bestAvailableAP = selectedData
    ? selectedData.ap['12mo'] !== null
      ? { value: selectedData.ap['12mo'], label: '12-month' }
      : selectedData.ap['6mo'] !== null
      ? { value: selectedData.ap['6mo'], label: '6-month' }
      : selectedData.ap['3mo'] !== null
      ? { value: selectedData.ap['3mo'], label: '3-month' }
      : null
    : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t('agentLifecycle.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
        >
          <Tab label={t('agentLifecycle.cohortAnalysis')} />
          <Tab label={t('agentLifecycle.manageAgents')} />
        </Tabs>
      </Paper>

      {/* ===== TAB 0: Cohort Analysis ===== */}
      {activeTab === 0 && (
        <>
      {/* Cohort Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('agentLifecycle.cohort')}</InputLabel>
              <Select
                value={selectedCohort}
                label={t('agentLifecycle.cohort')}
                onChange={(e) => setSelectedCohort(e.target.value)}
              >
                <MenuItem value="">{t('agentLifecycle.allCohorts')}</MenuItem>
                {cohorts.map((c) => (
                  <MenuItem key={c.cohort} value={c.cohort}>
                    {c.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography variant="body2" color="text.secondary">
              {selectedCohort
                ? `Viewing agents who joined in ${selectedData?.displayName} (${selectedData?.totalAgents} agents)`
                : `Showing all ${cohorts.length} cohort${cohorts.length !== 1 ? 's' : ''}`}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* KPI Cards — shown when a specific cohort is selected */}
      {selectedData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('agentLifecycle.totalAgents')}
                </Typography>
                <Typography variant="h4">{selectedData.totalAgents}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('agentLifecycle.currentlyActive')}
                </Typography>
                <Typography variant="h4">
                  {cohortDetail
                    ? cohortDetail.agents.filter((a) => a.status === 'active').length
                    : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('agentLifecycle.retentionRate')}
                </Typography>
                <Typography variant="h4">
                  {bestAvailableRetention && bestAvailableRetention.rate !== null
                    ? `${bestAvailableRetention.rate}%`
                    : '—'}
                </Typography>
                {bestAvailableRetention && (
                  <Typography variant="caption" color="text.secondary">
                    at{' '}
                    {selectedData.retention['12mo'].available
                      ? '12'
                      : selectedData.retention['6mo'].available
                      ? '6'
                      : '3'}{' '}
                    months
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('agentLifecycle.avgAPPerAgent')}
                </Typography>
                <Typography variant="h4">
                  {bestAvailableAP ? formatCurrency(bestAvailableAP.value) : '—'}
                </Typography>
                {bestAvailableAP && (
                  <Typography variant="caption" color="text.secondary">
                    cumulative ({bestAvailableAP.label})
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Detail loading spinner */}
      {selectedCohort && detailLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Charts — shown when a cohort is selected and data is loaded */}
      {selectedCohort && cohortDetail && !detailLoading && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Retention Curve */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('agentLifecycle.retentionCurve')}
              </Typography>
              {cohortDetail.retentionCurve.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={cohortDetail.retentionCurve}
                    margin={{ top: 5, right: 20, left: 0, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month"
                      tickFormatter={(v) => `M${v}`}
                      label={{ value: 'Month', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                      width={45}
                    />
                    <Tooltip
                      formatter={(v) => [`${v}%`, 'Retention']}
                      labelFormatter={(l) => `Month ${l}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="rate"
                      name="Retention %"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {t('agentLifecycle.notEnoughData')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Cumulative AP Curve */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('agentLifecycle.avgCumulativeAP')}
              </Typography>
              {cohortDetail.apCurve.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={cohortDetail.apCurve}
                    margin={{ top: 5, right: 20, left: 10, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month"
                      tickFormatter={(v) => `M${v}`}
                      label={{ value: 'Month', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                      width={55}
                    />
                    <Tooltip
                      formatter={(v) => [formatCurrency(v), 'Avg AP']}
                      labelFormatter={(l) => `Month ${l}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="avgAP"
                      name="Avg AP"
                      stroke={theme.palette.success.main}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {t('agentLifecycle.noAPData')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Agent Table — shown when a specific cohort is selected */}
      {selectedCohort && cohortDetail && !detailLoading && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">
              {t('agentLifecycle.agentsInCohort', { name: cohortDetail.displayName })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('agentLifecycle.clickStatusChip')}
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('agentLifecycle.email')}</TableCell>
                  <TableCell>{t('agentLifecycle.joinDate')}</TableCell>
                  <TableCell>{t('agentLifecycle.status')}</TableCell>
                  <TableCell>{t('agentLifecycle.leftDate')}</TableCell>
                  <TableCell align="right">{t('analytics.totalAP')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cohortDetail.agents.map((agent) => (
                  <TableRow key={agent.user_id} hover>
                    <TableCell>{agent.name}</TableCell>
                    <TableCell>{agent.email}</TableCell>
                    <TableCell>
                      {agent.created_at
                        ? new Date(agent.created_at).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={agent.status === 'active' ? t('common.active') : t('common.inactive')}
                        color={agent.status === 'active' ? 'success' : 'default'}
                        size="small"
                        onClick={() => openStatusDialog(agent)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      {agent.left_date
                        ? new Date(agent.left_date).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(agent.totalAP)}</TableCell>
                  </TableRow>
                ))}
                {cohortDetail.agents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {t('agentLifecycle.noAgentsInCohort')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Cohort Overview Table — shown in "All Cohorts" view */}
      {!selectedCohort && (
        <Paper>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{t('agentLifecycle.cohortOverview')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('agentLifecycle.clickRowToDrill')}
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('agentLifecycle.joinMonth')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.totalAgents')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.threeMonthRetention')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.sixMonthRetention')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.twelveMonthRetention')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.twentyFourMonthRetention')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.churnZeroToThree')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.churnThreeToSix')}</TableCell>
                  <TableCell align="right">{t('agentLifecycle.twelveMonthAvgAP')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cohorts.map((c) => (
                  <TableRow
                    key={c.cohort}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCohort(c.cohort)}
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{c.displayName}</Typography>
                    </TableCell>
                    <TableCell align="right">{c.totalAgents}</TableCell>
                    <TableCell align="right">{renderRetentionCell(c.retention['3mo'])}</TableCell>
                    <TableCell align="right">{renderRetentionCell(c.retention['6mo'])}</TableCell>
                    <TableCell align="right">{renderRetentionCell(c.retention['12mo'])}</TableCell>
                    <TableCell align="right">{renderRetentionCell(c.retention['24mo'])}</TableCell>
                    <TableCell align="right">{renderChurnCell(c.churn['0_3mo'])}</TableCell>
                    <TableCell align="right">{renderChurnCell(c.churn['3_6mo'])}</TableCell>
                    <TableCell align="right">
                      {c.ap['12mo'] !== null && c.ap['12mo'] !== undefined
                        ? formatCurrency(c.ap['12mo'])
                        : <Typography variant="body2" color="text.disabled">{t('common.tbd')}</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
                {cohorts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {t('agentLifecycle.noCohortData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Status Toggle Dialog */}
      <Dialog
        open={statusDialog.open}
        onClose={() => setStatusDialog({ open: false, agent: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('agentLifecycle.updateAgentStatus')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Agent: <strong>{statusDialog.agent?.name}</strong>
            <br />
            Current status: <strong>{statusDialog.agent?.status}</strong>
          </Typography>
          {statusDialog.agent?.status === 'active' && (
            <TextField
              label={t('agentLifecycle.leftDate')}
              type="date"
              value={leftDate}
              onChange={(e) => setLeftDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText={t('agentLifecycle.leftDateRequired')}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, agent: null })}>
            {t('common.cancel')}
          </Button>
          {statusDialog.agent?.status === 'active' ? (
            <Button
              color="error"
              variant="contained"
              onClick={() => handleStatusChange('inactive')}
              disabled={!leftDate}
            >
              {t('agentLifecycle.markInactive')}
            </Button>
          ) : (
            <Button
              color="success"
              variant="contained"
              onClick={() => handleStatusChange('active')}
            >
              {t('agentLifecycle.markActive')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
        </>
      )}

      {/* ===== TAB 1: Manage Agents ===== */}
      {activeTab === 1 && (
        <>
          {agentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6">{t('agentLifecycle.allAgentsTab')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('agentLifecycle.editAgentDescription')}
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.name')}</TableCell>
                      <TableCell>{t('agentLifecycle.email')}</TableCell>
                      <TableCell>{t('agentLifecycle.startDate')}</TableCell>
                      <TableCell>{t('agentLifecycle.status')}</TableCell>
                      <TableCell>{t('agentLifecycle.leftDate')}</TableCell>
                      <TableCell align="right">{t('analytics.totalAP')}</TableCell>
                      <TableCell align="center">{t('agentLifecycle.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allAgents.map((agent) => {
                      const isEditing = editingAgent === agent.user_id;
                      return (
                        <TableRow key={agent.user_id} hover>
                          <TableCell>{agent.name}</TableCell>
                          <TableCell>{agent.email}</TableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                type="date"
                                value={editForm.created_at}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, created_at: e.target.value })
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 160 }}
                              />
                            ) : (
                              agent.created_at
                                ? new Date(agent.created_at).toLocaleDateString()
                                : '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={editForm.status}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      status: e.target.value,
                                      left_date: e.target.value === 'active' ? '' : editForm.left_date,
                                    })
                                  }
                                >
                                  <MenuItem value="active">{t('common.active')}</MenuItem>
                                  <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Chip
                                label={agent.status === 'active' ? t('common.active') : t('common.inactive')}
                                color={agent.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                type="date"
                                value={editForm.left_date}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, left_date: e.target.value })
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                disabled={editForm.status === 'active'}
                                sx={{ width: 160 }}
                              />
                            ) : (
                              agent.left_date
                                ? new Date(agent.left_date).toLocaleDateString()
                                : '—'
                            )}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(agent.totalAP)}</TableCell>
                          <TableCell align="center">
                            {isEditing ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => saveAgent(agent.user_id)}
                                  disabled={savingAgent}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={cancelEditing}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => startEditing(agent)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {allAgents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>
                            {t('agentLifecycle.noAgentsFound')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default AgentLifecycle;
